import { CreditCard, Display, PlugConnection, Pulse } from '@gravity-ui/icons';
import { useNavigate } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { Funnel } from '@gravity-ui/icons';
import { Popover } from '@heroui/react';
import { Button, ChoiceSelect, Tick, Empty, ErrorNote, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, Stat, StatusMark } from '../components/ui.tsx';
import { SearchBox } from '../components/ui/SearchBox.tsx';
import { listCount, NoMatch, ShowMore, useListSearch, usePagedList } from '../pagedList.tsx';
import { useState } from 'react';
import { Explainer } from '../components/ui/Layout.tsx';
import { postureInWords, postureWeak } from '../components/meshWords.ts';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { OrderMark } from '../components/updates.tsx';
import { isCloud } from '../edition.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { useMoney } from '../money.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

const tone = VIEW_TONES.workbenches!;
const OS_NAME: Record<string, string> = { windows: 'Windows', win32: 'Windows', darwin: 'macOS', linux: 'Linux' };

export function WorkbenchesPage() {
  const navigate = useNavigate();
  const { format } = useMoney();
  // Open pushed updates per machine (an administrator's view; others see none).
  const orders = useLoad(() => (isCloud() ? Promise.resolve([]) : routes.updateOrders().catch(() => [])), [], 30_000);
  const pendingFor = (deviceId: string) => (orders.data ?? []).filter((o) => o.deviceId === deviceId && o.state === 'pending');
  // The ring a machine sits in and the channel that ring decided, so "why am I on a nightly?" has an answer on the row.
  const releases = useLoad(() => (isCloud() ? Promise.resolve(undefined) : routes.releases().catch(() => undefined)), []);
  const ringText = (deviceId: string): string | undefined => {
    const ringId = releases.data?.ringOf?.[deviceId];
    const ring = releases.data?.rings.find((r) => r.id === ringId);
    if (!ring) return undefined;
    const channel = ring.channel ?? releases.data?.products.find((p) => p.product === 'workbench')?.policy.channel;
    return channel === 'nightly' ? `${ring.name} ring · nightly builds` : ring.match ? `${ring.name} ring` : undefined;
  };
  // Searched and filtered on the server, a page at a time; the 15-second poll refreshes the first page only.
  const search = useListSearch();
  const [state, setState] = useState<'all' | 'online' | 'offline'>('all');
  const [os, setOs] = useState('all');
  const [update, setUpdate] = useState(false);
  const filters = { q: search.q, online: state === 'all' ? undefined : state === 'online', platform: os === 'all' ? undefined : os, update: update || undefined };
  const devices = usePagedList((cursor) => routes.devicesPage({ ...filters, cursor }), filters, { intervalMs: 15_000, keyOf: (d) => d.deviceId });
  const usage = useLoad(() => routes.usage(), [], 60_000);
  // Nodes register through the same route (their hostname, a node: principal); they have their own page, so only desktops are asked for.
  const rows = devices.items;
  const totals = devices.extra;
  const loaded = devices.loaded;
  const active = [state !== 'all', os !== 'all', update].filter(Boolean).length;
  const narrowed = Boolean(search.q) || active > 0;
  const clearAll = () => {
    search.clear();
    setState('all');
    setOs('all');
    setUpdate(false);
  };
  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<Display />}
        title="Workbenches"
        subtitle="Every AI Workbench enrolled in this organisation, and whether you can reach it right now."
        actions={
          <Button size="sm" variant="secondary" isPending={devices.loading && loaded} onPress={devices.reload}>
            Refresh
          </Button>
        }
      />
      <Explainer id="workbenches" tone={tone} icon={<Display />} title="How Workbenches works" summary="Each desktop that signed in to the organisation, and whether it answers">
        <p>A workbench joins when someone chooses Sign in to your organisation in AI Workbench and an approver accepts the code it shows. From then on it reports its workspaces, its agents and its health here.</p>
        <p>Connected means you can reach it now: open it to see its sessions, steer an agent or push an update. Online means it checked in recently but holds no live connection; offline means it has not been heard from for a while.</p>
      </Explainer>
      <PartBoundary key="workbenches" name="Workbenches" tone={tone}>
        <div className="art-stats">
          <Stat tone={tone} icon={<Display />} label="Enrolled" value={totals?.total} detail={totals ? `${totals.online} online` : ''} />
          <Stat tone={tone} icon={<PlugConnection />} label="Offline" value={totals ? totals.total - totals.online : undefined} detail="Not heard from lately" />
          <Stat tone={tone} icon={<Pulse />} label="Active agents" value={totals?.activeAgents} detail="Across the fleet" />
          <Stat tone={tone} icon={<CreditCard />} label={usage.data?.scope === 'me' ? 'Your spend, 30 days' : 'Spend, 30 days'} value={usage.data ? format(usage.data.costUsd) : undefined} detail={usage.data ? `${usage.data.calls.toLocaleString()} model calls${usage.data.scope === 'me' ? ' for you or on your machines' : ' across the organisation'}` : ''} />
        </div>
        <ErrorNote error={devices.error} />
        <ListGroup
          title="Machines"
          count={loaded ? listCount(rows.length, { one: 'workbench', many: 'workbenches' }, { total: narrowed ? undefined : totals?.total, hasMore: devices.hasMore, searched: narrowed }) : undefined}
          description="Open one to see its sessions, its workspaces and its updates."
          actions={
            <>
              <SearchBox label="Search workbenches" value={search.typed} onChange={search.setTyped} placeholder="Search workbenches" className="w-40 max-w-full @lg:w-56" />
              <Popover>
                <Button size="sm" variant="secondary" aria-label="Filter workbenches">
                  <Funnel className="size-4" />
                  Filter{active ? ` (${active})` : ''}
                </Button>
                <Popover.Content placement="bottom end" className="w-72 max-w-[calc(100vw-1rem)]">
                  <Popover.Dialog className="grid gap-3 p-3" aria-label="Filter workbenches">
                    <ChoiceSelect label="State" value={state} options={{ all: 'Any state', online: 'Online', offline: 'Offline' }} onChange={setState} className="w-full" />
                    <ChoiceSelect label="Operating system" value={os} options={{ all: 'Any system', windows: 'Windows', darwin: 'macOS', linux: 'Linux' }} onChange={setOs} className="w-full" />
                    <Tick checked={update} onChange={setUpdate}>
                      Update available
                    </Tick>
                    {active > 0 ? (
                      <Button size="sm" variant="ghost" onPress={() => (setState('all'), setOs('all'), setUpdate(false))}>
                        Clear filters
                      </Button>
                    ) : null}
                  </Popover.Dialog>
                </Popover.Content>
              </Popover>
            </>
          }
          empty={
            loaded && rows.length === 0 && narrowed ? (
              <NoMatch tone={tone} icon={<Display />} q={search.q} filtered={active > 0} noun="workbenches" onClear={clearAll} />
            ) : loaded && rows.length === 0 ? (
              <Empty
                tone={tone}
                icon={<Display />}
                title="No workbenches yet"
                body="Open AI Workbench, choose Sign in to your organisation, and approve the code it shows under Add a workbench."
                action={
                  <Button size="sm" variant="primary" onPress={() => navigate('/enrol')}>
                    Add a workbench
                  </Button>
                }
              />
            ) : undefined
          }
        >
          {!loaded ? <ListBlock className="text-muted">Asking the fleet…</ListBlock> : null}
          {rows.map((d) => {
            const workspaces = d.workspaces.map((w) => w.name).join(', ');
            const posture = postureInWords(d.posture);
            const notes = [ringText(d.deviceId), d.policyRevision ? `policy ${d.policyRevision}` : undefined].filter(Boolean).join(' · ');
            return (
              <ListRow
                key={d.deviceId}
                to={`/workbenches/${encodeURIComponent(d.deviceId)}`}
                icon={<Display />}
                title={d.hostname}
                description={
                  <>
                    <span className="block truncate" title={d.userEmail ?? d.userId}>
                      {[d.userEmail ?? d.userId, OS_NAME[d.platform] ?? d.platform, `version ${d.appVersion}`, `seen ${timeAgo(d.lastSeenAt)}`].join(' · ')}
                    </span>
                    <span className="block truncate" title={workspaces}>
                      {workspaces ? `${d.workspaces.length === 1 ? 'Workspace' : 'Workspaces'} ${workspaces}` : 'No workspace open'}
                      {d.activeAgents ? ` · ${d.activeAgents} ${d.activeAgents === 1 ? 'agent' : 'agents'} working` : ''}
                    </span>
                    {notes ? <span className="block truncate">{notes}</span> : null}
                    {postureWeak(d.posture) ? <span className="block text-warning">{posture}</span> : posture !== 'posture unknown' ? <span className="block truncate">{posture}</span> : null}
                    {d.unmanagedTeams ? (
                      <span className="mt-0.5 block">
                        <StatusMark state="medium" label={`${d.unmanagedTeams === 1 ? '1 team' : `${d.unmanagedTeams} teams`} not under management`} />
                      </span>
                    ) : null}
                    {pendingFor(d.deviceId).map((o) => (
                      <span key={o.id} className="mt-0.5 block">
                        <OrderMark o={o} />
                      </span>
                    ))}
                  </>
                }
                status={<StatusMark state={d.relayConnected ? 'connected' : d.online ? 'online' : 'offline'} />}
              />
            );
          })}
          {devices.hasMore ? <ShowMore onPress={devices.showMore} pending={devices.loadingMore} /> : null}
        </ListGroup>
      </PartBoundary>
    </PageColumn>
  );
}
